import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Calendar, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { cn } from './lib/cn';

// Platform date field (Gregorian, calendar-agnostic). Replaces the native
// <input type="date">, which renders the OS locale calendar — on a Saudi machine
// that's the Hijri calendar, which we do NOT want here. Value in/out is a plain
// ISO `YYYY-MM-DD` string so backend contracts are unchanged.
//
// Shared across all three apps (plan 021/C5) — previously two byte-identical
// copies. Because it lives outside any app's own source tree, each app's CSS
// must @source this package explicitly, or Tailwind never emits the utilities
// used only here.

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

// Panel size, used to decide which way it opens. Kept in sync with the w-64
// class and the rendered height (header + 6 week rows + footer).
const PANEL_W = 256;
const PANEL_H = 320;

function parseISO(v: string): { y: number; m: number; d: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v || '');
  if (!m) return null;
  return { y: +m[1], m: +m[2] - 1, d: +m[3] };
}
function toISO(y: number, m: number, d: number): string {
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}
function fmtDisplay(v: string): string {
  const p = parseISO(v);
  return p ? `${String(p.d).padStart(2, '0')} ${MONTHS[p.m].slice(0, 3)} ${p.y}` : '';
}

interface DateFieldProps {
  value: string;                       // '' or 'YYYY-MM-DD'
  onChange: (iso: string) => void;
  placeholder?: string;
  className?: string;
  clearable?: boolean;
}

export function DateField({ value, onChange, placeholder = 'Select date', className, clearable = true }: DateFieldProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selected = parseISO(value);

  // Which month the calendar is showing: the selected date, else the CURRENT
  // month. This was hardcoded to July 2026 — correct only during the month it
  // was written, and silently wrong every month after.
  const [view, setView] = useState(() => {
    if (selected) return selected;
    const now = new Date();
    return { y: now.getFullYear(), m: now.getMonth(), d: 1 };
  });
  useEffect(() => { if (selected) setView(selected); /* eslint-disable-next-line */ }, [value]);

  // Which way the calendar opens. It is 256×~310 and anchored to the trigger,
  // so a field near the right edge (e.g. the "To" of a From/To pair in a page
  // header) pushed it off-screen — you saw half a calendar. Decide per open,
  // from the space actually available.
  const [place, setPlace] = useState<{ right: boolean; up: boolean }>({ right: false, up: false });

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onEsc); };
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const MARGIN = 8;
    setPlace({
      // Flip only when the default side genuinely doesn't fit AND the other
      // side does — a field too narrow for either keeps the normal alignment.
      right: r.left + PANEL_W + MARGIN > window.innerWidth && r.right - PANEL_W >= MARGIN,
      up: r.bottom + PANEL_H + MARGIN > window.innerHeight && r.top - PANEL_H >= MARGIN,
    });
  }, [open]);

  const grid = useMemo(() => {
    const first = new Date(view.y, view.m, 1).getDay();          // 0=Sun
    const days = new Date(view.y, view.m + 1, 0).getDate();
    const cells: (number | null)[] = Array(first).fill(null);
    for (let d = 1; d <= days; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [view]);

  const step = (delta: number) => {
    const m = view.m + delta;
    setView({ y: view.y + Math.floor(m / 12), m: ((m % 12) + 12) % 12, d: 1 });
  };

  return (
    <div ref={ref} className={cn('relative', className)}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex h-9 w-full items-center gap-2 rounded-lg border border-border bg-card px-3 text-left text-xs text-foreground transition-colors hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-primary/30"
      >
        <Calendar className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <span className={cn('flex-1 tabular-nums', !value && 'text-muted-foreground')}>
          {value ? fmtDisplay(value) : placeholder}
        </span>
        {clearable && value && (
          <span
            role="button"
            tabIndex={0}
            aria-label="Clear date"
            onClick={(e) => { e.stopPropagation(); onChange(''); }}
            className="rounded p-0.5 text-muted-foreground hover:bg-stone-200 hover:text-foreground"
          >
            <X className="h-3 w-3" />
          </span>
        )}
      </button>

      {open && (
        <div
          className={cn(
            'absolute z-50 w-64 rounded-xl border border-border bg-card p-3 shadow-lg',
            place.right ? 'right-0' : 'left-0',
            place.up ? 'bottom-full mb-1' : 'mt-1',
          )}
        >
          <div className="mb-2 flex items-center justify-between">
            <button type="button" onClick={() => step(-1)} className="rounded-md p-1 hover:bg-secondary">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <div className="flex items-center gap-1 text-sm font-semibold">
              <span>{MONTHS[view.m]}</span>
              <span className="tabular-nums text-muted-foreground">{view.y}</span>
            </div>
            <button type="button" onClick={() => step(1)} className="rounded-md p-1 hover:bg-secondary">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          <div className="mb-1 grid grid-cols-7 text-center text-[10px] font-semibold uppercase text-muted-foreground">
            {WEEKDAYS.map((w) => <div key={w} className="py-1">{w}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-0.5">
            {grid.map((d, i) => {
              if (d === null) return <div key={i} />;
              const isSel = selected && selected.y === view.y && selected.m === view.m && selected.d === d;
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => { onChange(toISO(view.y, view.m, d)); setOpen(false); }}
                  className={cn(
                    'flex h-8 items-center justify-center rounded-md text-xs tabular-nums transition-colors',
                    isSel ? 'bg-primary font-semibold text-primary-foreground' : 'hover:bg-secondary',
                  )}
                >
                  {d}
                </button>
              );
            })}
          </div>

          <div className="mt-2 flex items-center justify-between border-t border-border pt-2 text-xs">
            <span className="text-muted-foreground">Gregorian</span>
            {clearable && (
              <button type="button" className="text-primary hover:underline" onClick={() => { onChange(''); setOpen(false); }}>
                Clear
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
